import axios, { AxiosError } from 'axios'
import { resolveApiBaseURL } from '@/lib/api/client'
import { usePlatformStore } from '@/stores/platform.store'
import type {
  CreatePlatformInviteDto,
  CreatePlatformInviteResult,
  CreatePlatformTenantDto,
  PlatformInvite,
  PlatformTenant,
  PlatformUser,
} from '@/types/platform'

const API_BASE = import.meta.env.VITE_API_URL || ''

function resolveBaseURL(): string {
  if (API_BASE) return API_BASE
  return resolveApiBaseURL()
}

export const platformClient = axios.create({
  baseURL: resolveBaseURL(),
})

platformClient.interceptors.request.use((config) => {
  const token = usePlatformStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

platformClient.interceptors.response.use(
  (response) => {
    const raw = response.data
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'success' in raw) {
      if (raw.success === false) {
        const msg = raw.error || raw.message || 'API Error'
        const err = new AxiosError(msg, 'API_ERROR', response.config, response.request, response)
        if (raw.code !== undefined) (err as unknown as { apiCode?: string }).apiCode = String(raw.code)
        if (raw.details !== undefined) (err as unknown as { apiDetails?: unknown }).apiDetails = raw.details
        return Promise.reject(err)
      }
      if (raw.success === true && 'data' in raw && raw.data !== undefined) {
        const payloadKeys = Object.keys(raw).filter((k) => k !== 'success' && k !== 'message')
        if (payloadKeys.length === 1 && payloadKeys[0] === 'data') {
          response.data = raw.data
        }
      }
    }
    return response
  },
  (error) => Promise.reject(error),
)

export const platformApi = {
  listTenants: () =>
    platformClient.get<{ tenants: PlatformTenant[]; total: number }>('/api/platform/tenants'),

  getTenant: (id: string) =>
    platformClient.get<{ tenant: PlatformTenant }>(`/api/platform/tenants/${id}`),

  listUsers: (tenantId: string) =>
    platformClient.get<{ users: PlatformUser[]; total: number }>(`/api/platform/tenants/${tenantId}/users`),

  listInvites: (tenantId: string) =>
    platformClient.get<{ invites: PlatformInvite[]; total: number }>(
      `/api/platform/tenants/${tenantId}/invites`,
    ),

  createTenant: (data: CreatePlatformTenantDto) =>
    platformClient.post<{ tenant: PlatformTenant }>('/api/platform/tenants', data),

  createInvite: (tenantId: string, data: CreatePlatformInviteDto) =>
    platformClient.post<CreatePlatformInviteResult>(`/api/platform/tenants/${tenantId}/invites`, data),

  /** Validates operator token (401 if invalid). */
  ping: () => platformClient.get('/api/platform/tenants'),
}
